import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Row, Col, Alert, Form } from 'react-bootstrap';
import { FaPrint, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUserTie, FaEdit, FaSave } from 'react-icons/fa';
import axios from 'axios';

const BookingDetailsModal = ({
  show,
  onHide,
  booking,
  onUpdate = () => {}
}) => {
  const [localBooking, setLocalBooking] = useState(null);
  const [isEditingReturn, setIsEditingReturn] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (booking) {
      setLocalBooking({ ...booking });
      setReturnDate(booking.returnPickupDate || '');
      setReturnTime(booking.returnPickupTime || '');
    }
  }, [booking]);

  if (!localBooking) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':   return <Badge bg="warning">Pending</Badge>;
      case 'confirmed': return <Badge bg="primary">Confirmed</Badge>;
      case 'assigned':  return <Badge bg="info">Assigned</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default:          return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const handleSaveReturnDetails = async () => {
    if (!returnDate || !returnTime) {
      setError('Both return date and time are required');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/bookings/${localBooking._id}/return-details`,
        { 
          returnPickupDate: returnDate, 
          returnPickupTime: returnTime 
        },
        { 
          headers: { 'x-auth-token': token } 
        }
      );
      
      if (response.data.success) {
        const updated = response.data.booking;
        setLocalBooking(prev => ({
          ...prev,
          returnPickupDate: updated.returnPickupDate,
          returnPickupTime: updated.returnPickupTime
        }));
        setSuccess('Return details updated successfully');
        setIsEditingReturn(false);
        setTimeout(() => setSuccess(null), 3000);
        onUpdate(updated);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update return details');
    } finally {
      setIsLoading(false);
    }
  };

  const renderAddons = (addons, prefix = '') => {
    if (!addons) return null;
    return (
      <>
        {addons.babySeat && (
          <tr>
            <td><strong>{prefix}Baby Seats:</strong></td>
            <td>
              {addons.babySeatDetails?.infant?.selected && (
                <div>Infant: {addons.babySeatDetails.infant.quantity} × ${addons.babySeatDetails.infant.price} = ${addons.babySeatDetails.infant.totalPrice}</div>
              )}
              {addons.babySeatDetails?.child?.selected && (
                <div>Child: {addons.babySeatDetails.child.quantity} × ${addons.babySeatDetails.child.price} = ${addons.babySeatDetails.child.totalPrice}</div>
              )}
              {addons.babySeatDetails?.booster?.selected && (
                <div>Booster: {addons.babySeatDetails.booster.quantity} × ${addons.babySeatDetails.booster.price} = ${addons.babySeatDetails.booster.totalPrice}</div>
              )}
              <div><strong>Total:</strong> ${addons.babySeatDetails?.totalBabySeatPrice || 0}</div>
            </td>
          </tr>
        )}
        {addons.wineBottle && (
          <tr>
            <td><strong>{prefix}Wine Bottle:</strong></td>
            <td>${addons.wineBottlePrice || 0}</td>
          </tr>
        )}
        {addons.redCarpet && (
          <tr>
            <td><strong>{prefix}Red Carpet:</strong></td>
            <td>${addons.redCarpetPrice || 0}</td>
          </tr>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Booking Details - {localBooking._id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="mb-4">
          <Col md={6}>
            <h5>Customer Information</h5>
            <Table bordered>
              <tbody>
                <tr>
                  <td><strong>Name:</strong></td>
                  <td>{localBooking.customerName}</td>
                </tr>
                <tr>
                  <td><strong>Email:</strong></td>
                  <td>{localBooking.customerEmail}</td>
                </tr>
                <tr>
                  <td><strong>Phone:</strong></td>
                  <td>{localBooking.customerPhone}</td>
                </tr>
                {localBooking.user && (
                  <tr>
                    <td><strong>User ID:</strong></td>
                    <td>{localBooking.user._id}</td>
                  </tr>
                )}
                <tr>
                  <td><strong>IP Address:</strong></td>
                  <td>{localBooking.ipAddress || 'N/A'}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col md={6}>
            <h5>Booking Information</h5>
            <Table bordered>
              <tbody>
                <tr>
                  <td><strong>Status:</strong></td>
                  <td>{getStatusBadge(localBooking.bookingStatus)}</td>
                </tr>
                <tr>
                  <td><strong>Booking Date:</strong></td>
                  <td>{formatDate(localBooking.createdAt)}</td>
                </tr>
                <tr>
                  <td><strong>Pickup Date:</strong></td>
                  <td>{formatDate(localBooking.pickupDate)}</td>
                </tr>
                <tr>
                  <td><strong>Pickup Time:</strong></td>
                  <td>{localBooking.pickupTime}</td>
                </tr>
                <tr>
                  <td><strong>Vehicle:</strong></td>
                  <td>{localBooking.vehicleName}</td>
                </tr>
                <tr>
                  <td><strong>Passengers:</strong></td>
                  <td>{localBooking.numberOfPassengers}</td>
                </tr>
                <tr>
                  <td><strong>Luggage:</strong></td>
                  <td>{localBooking.numberOfLuggage}</td>
                </tr>
                {localBooking.flightNumber && (
                  <tr>
                    <td><strong>Flight Number:</strong></td>
                    <td>{localBooking.flightNumber}</td>
                  </tr>
                )}
                {localBooking.couponCode && (
                  <tr>
                    <td><strong>Coupon Code:</strong></td>
                    <td>{localBooking.couponCode} (${localBooking.couponDiscount} discount)</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        {localBooking.driver && (
          <Row className="mb-4">
            <Col>
              <h5>Driver Information</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <td><strong><FaUserTie className="me-2" />Driver Name:</strong></td>
                    <td>{localBooking.driver.name}</td>
                  </tr>
                  <tr>
                    <td><strong><FaPhone className="me-2" />Phone:</strong></td>
                    <td>{localBooking.driver.phone}</td>
                  </tr>
                  <tr>
                    <td><strong><FaEnvelope className="me-2" />Email:</strong></td>
                    <td>{localBooking.driver.email}</td>
                  </tr>
                  <tr>
                    <td><strong>License Number:</strong></td>
                    <td>{localBooking.driver.licenseNumber}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <Col md={6}>
            <h5>Trip Details</h5>
            <Table bordered>
              <tbody>
                <tr>
                  <td><strong>Pickup Location:</strong></td>
                  <td>
                    <FaMapMarkerAlt className="me-1 text-danger" />
                    {localBooking.pickupLocation}
                  </td>
                </tr>
                <tr>
                  <td><strong>Dropoff Location:</strong></td>
                  <td>
                    <FaMapMarkerAlt className="me-1 text-primary" />
                    {localBooking.dropoffLocation}
                  </td>
                </tr>
                {localBooking.stops && localBooking.stops.length > 0 && (
                  <tr>
                    <td><strong>Stops:</strong></td>
                    <td>
                      <ul className="mb-0">
                        {localBooking.stops.map((stop, index) => (
                          <li key={index}>{stop}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Distance:</strong></td>
                  <td>{localBooking.DistanceKm} km</td>
                </tr>
                <tr>
                  <td><strong>Duration:</strong></td>
                  <td>{localBooking.duration}</td>
                </tr>
                <tr>
                  <td><strong>Stop Fee:</strong></td>
                  <td>${localBooking.stopFee || 0}</td>
                </tr>
                <tr>
                  <td><strong>Base Price:</strong></td>
                  <td>${localBooking.basePrice}</td>
                </tr>
                {localBooking.overcharge > 0 && (
                  <tr>
                    <td><strong>Overcharge:</strong></td>
                    <td>${localBooking.overcharge}</td>
                  </tr>
                )}
                {localBooking.eventAdditionalPrice > 0 && (
                  <tr>
                    <td><strong>Event Surcharge:</strong></td>
                    <td>${localBooking.eventAdditionalPrice}</td>
                  </tr>
                )}
                {localBooking.lateNightCharge > 0 && (
                  <tr>
                    <td><strong>Late Night Charge:</strong></td>
                    <td>${localBooking.lateNightCharge}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
          <Col md={6}>
            <h5>Payment Information</h5>
            <Table bordered>
              <tbody>
                <tr>
                  <td><strong>Total Amount:</strong></td>
                  <td>${localBooking.totalAmount}</td>
                </tr>
                <tr>
                  <td><strong>Payment Method:</strong></td>
                  <td>{localBooking.paymentMethod === 'card' ? 'Credit Card' : 'Cash'}</td>
                </tr>
                {localBooking.paymentMethod === 'card' && localBooking.paymentDetails && (
                  <>
                    <tr>
                      <td><strong>Card Brand:</strong></td>
                      <td>{localBooking.paymentDetails.cardBrand}</td>
                    </tr>
                    <tr>
                      <td><strong>Last 4 Digits:</strong></td>
                      <td>**** **** **** {localBooking.paymentDetails.cardLast4}</td>
                    </tr>
                    <tr>
                      <td><strong>Expiry:</strong></td>
                      <td>{localBooking.paymentDetails.cardExpMonth}/{localBooking.paymentDetails.cardExpYear}</td>
                    </tr>
                    {localBooking.paymentDetails.refunded && (
                      <tr>
                        <td><strong>Refund Amount:</strong></td>
                        <td className="text-danger">-${localBooking.paymentDetails.refundAmount}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </Table>

            <h5 className="mt-4">Addons</h5>
            <Table bordered>
              <tbody>
                {renderAddons(localBooking.addons)}
              </tbody>
            </Table>
          </Col>
        </Row>

        {localBooking.notes && (
          <div className="mb-4">
            <h5>Special Notes</h5>
            <div className="p-3 bg-light rounded">
              {localBooking.notes}
            </div>
          </div>
        )}

        {localBooking.isReturnBooking && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5>Return Trip Details</h5>
              {!isEditingReturn ? (
                <Button variant="outline-primary" size="sm" onClick={() => setIsEditingReturn(true)}>
                  <FaEdit className="me-1" /> Edit
                </Button>
              ) : (
                <div>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    onClick={handleSaveReturnDetails} 
                    disabled={isLoading}
                    className="me-2"
                  >
                    <FaSave className="me-1" /> {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => setIsEditingReturn(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td><strong>Return Pickup:</strong></td>
                  <td>{localBooking.returnPickupLocation}</td>
                </tr>
                <tr>
                  <td><strong>Return Dropoff:</strong></td>
                  <td>{localBooking.returnDropoffLocation}</td>
                </tr>
                <tr>
                  <td><strong>Return Date:</strong></td>
                  <td>
                    {isEditingReturn ? (
                      <Form.Control 
                        type="date" 
                        value={returnDate} 
                        onChange={e => setReturnDate(e.target.value)} 
                        required 
                      />
                    ) : (
                      formatDate(returnDate)
                    )}
                  </td>
                </tr>
                <tr>
                  <td><strong>Return Time:</strong></td>
                  <td>
                    {isEditingReturn ? (
                      <Form.Control 
                        type="time" 
                        value={returnTime} 
                        onChange={e => setReturnTime(e.target.value)} 
                        required 
                      />
                    ) : (
                      returnTime
                    )}
                  </td>
                </tr>
                <tr>
                  <td><strong>Return Vehicle:</strong></td>
                  <td>{localBooking.returnVehicleName}</td>
                </tr>
                <tr>
                  <td><strong>Return Passengers:</strong></td>
                  <td>{localBooking.returnNumberOfPassengers}</td>
                </tr>
                <tr>
                  <td><strong>Return Luggage:</strong></td>
                  <td>{localBooking.returnNumberOfLuggage}</td>
                </tr>
                {localBooking.returnStops && localBooking.returnStops.length > 0 && (
                  <tr>
                    <td><strong>Return Stops:</strong></td>
                    <td>
                      <ul className="mb-0">
                        {localBooking.returnStops.map((stop, index) => (
                          <li key={index}>{stop}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Return Distance:</strong></td>
                  <td>{localBooking.returnDistanceKm} km</td>
                </tr>
                <tr>
                  <td><strong>Return Duration:</strong></td>
                  <td>{localBooking.returnDuration}</td>
                </tr>
                <tr>
                  <td><strong>Return Stop Fee:</strong></td>
                  <td>${localBooking.returnStopFee || 0}</td>
                </tr>
                <tr>
                  <td><strong>Return Base Price:</strong></td>
                  <td>${localBooking.returnBasePrice}</td>
                </tr>
                {localBooking.returnOvercharge > 0 && (
                  <tr>
                    <td><strong>Return Overcharge:</strong></td>
                    <td>${localBooking.returnOvercharge}</td>
                  </tr>
                )}
                {localBooking.returnEventAdditionalPrice > 0 && (
                  <tr>
                    <td><strong>Return Event Surcharge:</strong></td>
                    <td>${localBooking.returnEventAdditionalPrice}</td>
                  </tr>
                )}
                {localBooking.returnLateNightCharge > 0 && (
                  <tr>
                    <td><strong>Return Late Night Charge:</strong></td>
                    <td>${localBooking.returnLateNightCharge}</td>
                  </tr>
                )}
                <tr>
                  <td><strong>Return Total Cost:</strong></td>
                  <td>${localBooking.returnTotalCost}</td>
                </tr>
                <tr>
                  <td><strong>One Way Total Cost:</strong></td>
                  <td>${localBooking.onewayTotalCost}</td>
                </tr>
              </tbody>
            </Table>
            <h5 className="mt-4">Return Addons</h5>
            <Table bordered>
              <tbody>{renderAddons(localBooking.returnAddons, 'Return ')}</tbody>
            </Table>
          </div>
        )}

        {localBooking.cancellationReason && (
          <div className="mb-4">
            <h5>Cancellation Reason</h5>
            <div className="p-3 bg-light rounded">
              {localBooking.cancellationReason}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary">
          <FaPrint className="me-1" /> Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BookingDetailsModal;